if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.handleAlertOnlyFadeEnd = this.handleAlertOnlyFadeEnd.bind(this);
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.form = this.querySelector('form');
        if (!this.form) return;

        const variantIdInput = this.variantIdInput;
        if (variantIdInput) variantIdInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler);
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton?.querySelector('.product-form__submit-text') ||
          this.submitButton?.querySelector('span');

        if (
          document.querySelector('cart-drawer') &&
          this.submitButton &&
          this.shouldOpenCartDrawer()
        ) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.requiresQuickAddSizeSelection()) {
          this.showQuickAddSizeAlert();
          return;
        }

        if (!this.closest('[data-inline-product-row]')) {
          const productInfo = this.closest('product-info');
          if (
            productInfo &&
            window.SizeSelection?.requiresSelection(productInfo) &&
            (!window.SizeSelection.isSizeSelected(productInfo) ||
              !this.variantIdInput?.value)
          ) {
            return;
          }
        }

        if (this.isAlertOnly()) this.resetAlertOnlyState();

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('is-loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Read variant ID directly from input to ensure we have the latest value
        // This prevents race conditions where variant update hasn't completed yet
        const variantId = this.variantIdInput?.value;
        
        const formData = new FormData(this.form);
        
        // Ensure the variant ID in FormData matches the current input value
        if (variantId) {
          formData.set('id', variantId);
        }
        
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map(section => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal =
              this.closest('quick-add-modal') ||
              this.closest('quick-add-modal-mobile');
            const shouldOpenCart = this.shouldOpenCartDrawer();
            const renderOptions = shouldOpenCart ? {} : { open: false };

            if (quickAddModal) {
              quickAddModal.hide(true, {
                onClosed: meta => {
                  const quickAddOpen = Boolean(
                    document.querySelector(
                      'quick-add-modal[open], quick-add-modal.is-opening, quick-add-modal-mobile[open], quick-add-modal-mobile.is-opening'
                    )
                  );
                  const openDrawer =
                    shouldOpenCart && !meta?.interrupted && !quickAddOpen;
                  this.cart.renderContents(
                    response,
                    openDrawer ? {} : { open: false }
                  );
                },
              });
            } else {
              this.cart.renderContents(response, renderOptions);
              if (!shouldOpenCart) this.pendingAlertOnlySuccess = true;
            }
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('is-loading');
            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (this.pendingAlertOnlySuccess) {
              this.pendingAlertOnlySuccess = false;
              this.showAlertOnlySuccess();
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      isAlertOnly() {
        return this.submitButton?.hasAttribute('data-alert-only');
      }

      shouldOpenCartDrawer() {
        if (this.isAlertOnly()) return false;

        // Open the drawer only after add-to-cart inside the quick add modal/sheet.
        if (
          this.closest('quick-add-modal') ||
          this.closest('quick-add-modal-mobile')
        ) {
          return true;
        }

        // Card quick add submits update the cart without opening the drawer.
        if (
          this.form?.classList.contains('quick-add-slim__form') ||
          this.closest('.quick-add--desktop') ||
          this.closest('.quick-add--mobile')
        ) {
          return false;
        }

        return true;
      }

      requiresQuickAddSizeSelection() {
        return (
          this.form?.classList.contains('quick-add-slim__form') &&
          this.form?.dataset?.requiresSize === 'true' &&
          !this.variantIdInput?.value
        );
      }

      showQuickAddSizeAlert() {
        if (!this.submitButtonText) return;

        clearTimeout(this.quickAddSizeAlertTimeout);

        if (!this.quickAddSizeAlertMarkup) {
          this.quickAddSizeAlertMarkup = this.submitButtonText.innerHTML;
        }

        this.submitButtonText.textContent =
          window.variantStrings?.pleaseChooseASize || 'Please choose a size';
        this.submitButton.classList.add('is-size-alert');

        this.quickAddSizeAlertTimeout = setTimeout(() => {
          this.resetQuickAddSizeAlert();
        }, 2000);
      }

      resetQuickAddSizeAlert() {
        clearTimeout(this.quickAddSizeAlertTimeout);
        this.quickAddSizeAlertTimeout = null;

        if (this.quickAddSizeAlertMarkup && this.submitButtonText) {
          this.submitButtonText.innerHTML = this.quickAddSizeAlertMarkup;
        }

        this.submitButton?.classList.remove('is-size-alert');
      }

      showAlertOnlySuccess() {
        if (!this.submitButtonText) return;

        this.resetAlertOnlyState({ skipTextReset: true });

        this.submitButtonText.textContent =
          window.variantStrings?.addedToCart || 'Added to cart';
        this.submitButton.classList.add('is-alert-success');

        this.alertOnlyFadeTimeout = setTimeout(() => {
          this.submitButton.classList.add('is-alert-fading');
          this.submitButtonText.addEventListener(
            'transitionend',
            this.handleAlertOnlyFadeEnd,
            { once: true }
          );
          this.alertOnlyResetFallback = setTimeout(
            () => this.resetAlertOnlyState(),
            400
          );
        }, 1000);
      }

      handleAlertOnlyFadeEnd(event) {
        if (event.propertyName !== 'opacity') return;
        this.resetAlertOnlyState();
      }

      resetAlertOnlyState(options = {}) {
        clearTimeout(this.alertOnlyFadeTimeout);
        clearTimeout(this.alertOnlyResetFallback);
        this.submitButtonText?.removeEventListener(
          'transitionend',
          this.handleAlertOnlyFadeEnd
        );
        this.submitButton?.classList.remove('is-alert-success', 'is-alert-fading');

        if (!options.skipTextReset && this.submitButtonText) {
          this.submitButtonText.textContent =
            window.variantStrings?.addToCart || 'Add to cart';
        }
      }
    }
  );
}
